import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Button, Card, Icon, Input, Spinner, StatusPill, Textarea, useToast
} from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface BusinessRow {
  id: string;
  name: string;
  slug: string;
  is_public: number;
  onboarding_step: number;
  gallery: string | null;
  description: string | null;
}

interface ProRow {
  id: string;
  name: string;
  slug: string;
  title: string | null;
  bio: string | null;
  specialties: string | null;
  photo_path: string | null;
  whatsapp_phone: string | null;
  is_public: number;
}

interface Fail { status: number; message: string }

function toFail(err: unknown): Fail {
  if (err instanceof ApiError) return { status: err.status, message: err.message };
  return { status: 0, message: err instanceof Error ? err.message : 'Não foi possível concluir a operação.' };
}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_BYTES = 5 * 1024 * 1024;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
    reader.readAsDataURL(file);
  });
}

function parsePortfolio(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function parseSpecialties(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function normalizePhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 11 || digits.length === 10) return `55${digits}`;
  if (digits.length === 12 && digits.startsWith('55')) return digits;
  if (digits.length >= 8) return digits;
  return null;
}

export function PanelPublicProfilePage(): JSX.Element {
  usePageMeta(tr('Perfil público'), tr('Dados exibidos no seu perfil público, portfólio e publicação.'), '/painel/perfil');
  const { active } = useAuth();
  const toast = useToast();
  const bid = active?.businessId ?? '';
  const role = active?.role ?? 'professional';
  const perms = active?.permissions ?? [];
  const canPublish = role === 'owner' || role === 'manager';
  const canEditPortfolio = role === 'owner' || (role === 'manager' && (perms.includes('*') || perms.includes('business_settings')));
  const canEditSelf = Boolean(active?.professionalId);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Fail | null>(null);
  const [business, setBusiness] = useState<BusinessRow | null>(null);
  const [me, setMe] = useState<ProRow | null>(null);
  const [actionError, setActionError] = useState<Fail | null>(null);
  const [savingSelf, setSavingSelf] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);

  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [photoPath, setPhotoPath] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);
  const portfolioInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    setLoadError(null);
    try {
      const b = await api.get<{ business: BusinessRow }>(`/api/businesses/${bid}`);
      setBusiness(b.business);
      const t = await api.get<{ team: ProRow[] }>(`/api/team/${bid}/team`);
      const self = t.team.find((p) => p.id === active?.professionalId) ?? null;
      setMe(self);
      if (self) {
        setTitle(self.title ?? '');
        setBio(self.bio ?? '');
        setSpecialties(parseSpecialties(self.specialties).join(', '));
        setWhatsapp(self.whatsapp_phone ?? '');
        setIsPublic(self.is_public === 1);
        setPhotoPath(self.photo_path);
      }
    } catch (err) {
      setLoadError(toFail(err));
    } finally {
      setLoading(false);
    }
  }, [bid, active?.professionalId]);

  useEffect(() => {
    void load();
  }, [load]);

  const portfolio = useMemo(() => parsePortfolio(business?.gallery ?? null), [business]);
  const profileUrl = active?.professionalSlug ? `/profissional/${active.professionalSlug}` : '';

  async function uploadImage(file: File): Promise<string | null> {
    if (!ACCEPTED.includes(file.type)) {
      setActionError({ status: 0, message: tr('Formato não suportado. Use JPG, PNG, WEBP ou GIF.') });
      return null;
    }
    if (file.size > MAX_BYTES) {
      setActionError({ status: 0, message: tr('A imagem excede 5 MB. Escolha um arquivo menor.') });
      return null;
    }
    const dataUrl = await readFileAsDataUrl(file);
    const res = await api.post<{ path: string }>(`/api/businesses/${bid}/upload`, { filename: file.name, data: dataUrl });
    return res.path;
  }

  async function onPhotoPick(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingPhoto(true);
    setActionError(null);
    try {
      const path = await uploadImage(file);
      if (path) {
        setPhotoPath(path);
        toast(tr('Foto enviada. Salve as alterações para aplicar.'), 'success');
      }
    } catch (err) {
      setActionError(toFail(err));
    } finally {
      setUploadingPhoto(false);
    }
  }

  async function onPortfolioPick(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    const room = 30 - portfolio.length;
    if (room <= 0) {
      setActionError({ status: 0, message: tr('Limite de 30 fotos no portfólio atingido.') });
      return;
    }
    const selected = files.slice(0, room);
    setUploadingPortfolio(true);
    setActionError(null);
    try {
      const paths: string[] = [];
      for (const file of selected) {
        const path = await uploadImage(file);
        if (path) paths.push(path);
      }
      if (paths.length > 0) {
        const next = [...portfolio, ...paths];
        await api.patch(`/api/businesses/${bid}`, { gallery: next });
        setBusiness((prev) => (prev ? { ...prev, gallery: JSON.stringify(next) } : prev));
        toast(
          paths.length === 1
            ? tr('{count} foto adicionada ao portfólio.', { count: paths.length })
            : tr('{count} fotos adicionadas ao portfólio.', { count: paths.length }),
          'success'
        );
      }
      if (files.length > room) {
        setActionError({ status: 0, message: tr('O portfólio aceita até 30 fotos. {count} arquivo(s) não foram enviados.', { count: files.length - room }) });
      }
    } catch (err) {
      setActionError(toFail(err));
    } finally {
      setUploadingPortfolio(false);
    }
  }

  async function removePortfolioPhoto(path: string): Promise<void> {
    const next = portfolio.filter((g) => g !== path);
    setActionError(null);
    try {
      await api.patch(`/api/businesses/${bid}`, { gallery: next });
      setBusiness((prev) => (prev ? { ...prev, gallery: JSON.stringify(next) } : prev));
      toast(tr('Foto removida do portfólio.'), 'success');
    } catch (err) {
      setActionError(toFail(err));
    }
  }

  async function saveSelf(): Promise<void> {
    setSavingSelf(true);
    setActionError(null);
    try {
      await api.patch(`/api/businesses/${bid}/professional-me`, {
        title: title.trim(),
        bio: bio.trim(),
        specialties: specialties.split(',').map((s) => s.trim()).filter(Boolean),
        photoPath: photoPath ?? '',
        whatsappPhone: whatsapp.trim(),
        isPublic
      });
      toast('Perfil atualizado.', 'success');
      await load();
    } catch (err) {
      setActionError(toFail(err));
    } finally {
      setSavingSelf(false);
    }
  }

  async function togglePublish(): Promise<void> {
    if (!business) return;
    setPublishing(true);
    setActionError(null);
    try {
      if (business.is_public) {
        await api.post(`/api/businesses/${bid}/unpublish`);
        toast(tr('Perfil despublicado.'), 'success');
      } else {
        await api.post(`/api/businesses/${bid}/publish`);
        toast(tr('Perfil publicado com sucesso.'), 'success');
      }
      await load();
    } catch (err) {
      setActionError(toFail(err));
    } finally {
      setPublishing(false);
    }
  }

  async function copyLink(): Promise<void> {
    if (!profileUrl) return;
    const full = `${window.location.origin}${profileUrl}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      toast(tr('Link copiado.'), 'success');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setActionError({ status: 0, message: tr('Não foi possível copiar automaticamente. Link: {link}', { link: full }) });
    }
  }

  const phonePreview = normalizePhone(whatsapp);
  const phoneInvalid = whatsapp.trim().length > 0 && (phonePreview === null || phonePreview.length < 12);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Perfil público")}</h1>
          <p className="text-sm text-muted">{tr("O que os clientes veem em")} <code>{tr("/profissional/…")}</code>.</p>
        </div>
        {business ? (
          <StatusPill tone={business.is_public ? 'positive' : 'neutral'}>
            {business.is_public ? tr('Estabelecimento publicado') : tr('Não publicado')}
          </StatusPill>
        ) : null}
      </div>

      {loadError ? <ErrorAlert error={loadError} /> : null}
      {actionError ? <ErrorAlert error={actionError} /> : null}

      {loading ? (
        <Card><Spinner label={tr("Carregando perfil…")} /></Card>
      ) : !me ? (
        <EmptyBox text="Nenhum perfil profissional vinculado à sua conta neste estabelecimento." />
      ) : (
        <>
          <Card as="section">
            <div className="flex flex-wrap items-center gap-4">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-line bg-surface">
                {photoPath ? (
                  <img src={photoPath} alt={`Foto de ${me.name}`} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted"><Icon name="user" size={24} /></div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{me.name}</p>
                {active?.professionalSlug ? (
                  <p className="truncate text-sm text-muted">{window.location.origin}{tr("/profissional/")}{active.professionalSlug}</p>
                ) : <p className="text-sm text-muted">{tr("Sem link de perfil disponível.")}</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" icon={<Icon name="check" />} onClick={() => void copyLink()} disabled={!profileUrl}>
                  {copied ? tr('Copiado!') : tr('Copiar link')}
                </Button>
                <Button variant="secondary" onClick={() => photoInput.current?.click()} loading={uploadingPhoto}>{tr("Trocar foto")}</Button>
                <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => void onPhotoPick(e)} />
              </div>
            </div>

            {canPublish ? (
              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4">
                <Button onClick={() => void togglePublish()} loading={publishing}>
                  {business?.is_public ? tr('Despublicar') : tr('Publicar perfil')}
                </Button>
                <p className="text-sm text-muted">
                  {tr("Publicar exige ao menos um serviço ativo e o expediente configurado.")} {business?.is_public ? 'O estabelecimento está visível na busca.' : 'No ar, seu negócio aparece na busca.'}
                </p>
              </div>
            ) : (
              <Alert tone="info" title={tr("Publicação restrita")}>{tr("Apenas proprietário ou gerente pode publicar ou despublicar o estabelecimento.")}</Alert>
            )}
          </Card>

          <Card as="section">
            <h2 className="mb-3 text-lg font-semibold">{tr("Dados exibidos")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="nl-label">{tr("Título profissional")}</span>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canEditSelf} placeholder={tr("Ex.: Nail designer especialista")} />
              </label>
              <label className="block sm:col-span-1">
                <span className="nl-label">{tr("Telefone / WhatsApp (internacional)")}</span>
                <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} disabled={!canEditSelf} invalid={phoneInvalid} placeholder="5511999998888" />
                <span className={`nl-hint mt-1 block ${phoneInvalid ? 'text-danger' : ''}`}>
                  {phoneInvalid
                    ? tr('Informe um número válido. Formato internacional: 55 + DDD + número.')
                    : phonePreview ? tr('Será salvo como: {phone}', { phone: phonePreview }) : tr('Opcional. Formato 55 + DDD + número.')}
                </span>
              </label>
              <label className="block sm:col-span-2">
                <span className="nl-label">{tr("Bio")}</span>
                <Textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={4} disabled={!canEditSelf} maxLength={2000} />
              </label>
              <label className="block sm:col-span-2">
                <span className="nl-label">{tr("Especialidades (separadas por vírgula)")}</span>
                <Input value={specialties} onChange={(e) => setSpecialties(e.target.value)} disabled={!canEditSelf} placeholder={tr("Gel, Blindagem, Spa dos pés")} />
              </label>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} disabled={!canEditSelf} />
                {tr("Aparecer como profissional disponível na busca")}
              </label>
            </div>
            {canEditSelf ? (
              <div className="mt-4">
                <Button onClick={() => void saveSelf()} loading={savingSelf}>{tr("Salvar dados do perfil")}</Button>
              </div>
            ) : (
              <Alert tone="info" title={tr("Sem vínculo")}>{tr("Sua conta não possui um perfil profissional neste estabelecimento para editar.")}</Alert>
            )}
          </Card>

          <Card as="section">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold">{tr("Portfólio (")}{portfolio.length}/30)</h2>
                <p className="text-sm text-muted">{tr("Fotos dos seus trabalhos exibidas no perfil público.")}</p>
              </div>
              {canEditPortfolio ? (
                <div className="flex items-center gap-2">
                  <Button variant="secondary" onClick={() => portfolioInput.current?.click()} loading={uploadingPortfolio}>{tr("Enviar fotos")}</Button>
                  <input
                    ref={portfolioInput}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => void onPortfolioPick(e)}
                  />
                </div>
              ) : null}
            </div>
            {portfolio.length === 0 ? (
              <EmptyBox text="Nenhuma foto no portfólio ainda. Selecione várias fotos dos seus trabalhos de uma vez (JPEG, PNG, WEBP ou GIF, até 5 MB cada)." />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {portfolio.map((g) => (
                  <div key={g} className="relative overflow-hidden rounded-xl border border-line">
                    <img src={g} alt={tr("Trabalho do portfólio")} className="aspect-square w-full object-cover" />
                    {canEditPortfolio ? (
                      <button
                        type="button"
                        onClick={() => void removePortfolioPhoto(g)}
                        aria-label={tr("Remover foto do portfólio")}
                        className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white hover:bg-black/80"
                      >
                        <Icon name="close" size={14} />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            {!canEditPortfolio ? <p className="nl-hint mt-2">{tr("Gerenciar o portfólio é permitido ao proprietário e a gerentes com permissão de configurações.")}</p> : null}
          </Card>
        </>
      )}
    </div>
  );
}

function ErrorAlert({ error }: { error: Fail }): JSX.Element {
  const tone: 'warning' | 'danger' = error.status === 402 || error.status === 403 ? 'warning' : 'danger';
  const title = error.status === 402 ? 'Assinatura necessária' : error.status === 403 ? 'Permissão insuficiente' : 'Não foi possível concluir';
  return (
    <Alert tone={tone} title={title}>
      <p>{error.message}</p>
      {error.status === 402 ? <p className="mt-1">{tr("Contrate um plano em")} <Link className="font-semibold underline" to="/painel/plano">{tr("Meu plano")}</Link>.</p> : null}
    </Alert>
  );
}

function EmptyBox({ text }: { text: string }): JSX.Element {
  return <div className="rounded-2xl border border-dashed border-line bg-card px-5 py-8 text-center text-sm text-muted">{text}</div>;
}
